import React, { useContext, useMemo } from "react";
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
    DmsAltBox,
    DmsButton,
    DmsPage,
    intl,
    LanguageBaseContext,
    Notifications,
} from "dmsbasereact";
import { getBaseURL } from "../../../build_config";
import { getToken } from "dmsbasereact";
import { useExportJobsContext } from "../ExportJobsContext";
import { asyncExportApi } from "../api";
import { EXPORT_JOB_STATUS, EXPORT_STATUS_COLOR } from "../constants";

const MyDownloadsPage = () => {
    const { language } = useContext(LanguageBaseContext);
    const { jobs, loading, dismiss, refresh } = useExportJobsContext();

    const statusLabels = useMemo(
        () => ({
            [EXPORT_JOB_STATUS.PENDING]: intl.get("ExportJob.Status.Pending").d("Pending"),
            [EXPORT_JOB_STATUS.PROCESSING]: intl.get("ExportJob.Status.Processing").d("Processing"),
            [EXPORT_JOB_STATUS.READY]: intl.get("ExportJob.Status.Ready").d("Ready"),
            [EXPORT_JOB_STATUS.ERROR]: intl.get("ExportJob.Status.Error").d("Error"),
            [EXPORT_JOB_STATUS.EXPIRED]: intl.get("ExportJob.Status.Expired").d("Expired"),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [language]
    );

    const handleDownload = async (job) => {
        const token = getToken();
        const url = `${getBaseURL().replace(/\/$/, "")}${asyncExportApi.getDownloadUrl(job.pk)}`;
        const headers = token?.access_token
            ? { Authorization: `Bearer ${token.access_token}` }
            : {};

        try {
            const response = await fetch(url, { headers });
            if (!response.ok) {
                let message = intl.get("ExportJob.DownloadFailed").d("Download failed. Please try again.");
                if (response.status === 403) {
                    message = intl.get("ExportJob.DownloadForbidden").d("You are not authorized to download this file.");
                } else if (response.status === 410) {
                    message = intl.get("ExportJob.DownloadExpired").d("This file has expired and is no longer available.");
                } else if (response.status === 409) {
                    message = intl.get("ExportJob.DownloadNotReady").d("The file is not ready yet. Please wait.");
                }
                Notifications.toast({ title: message, type: Notifications.TYPE_ERROR, timeout: 6000 });
                return;
            }
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = blobUrl;
            anchor.download = job.file_name || `export_${job.pk}`;
            anchor.click();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed:", err);
            Notifications.toast({
                title: intl.get("ExportJob.DownloadFailed").d("Download failed. Please try again."),
                type: Notifications.TYPE_ERROR,
                timeout: 6000,
            });
        }
    };

    const handleDismissGroup = async (groupJobs) => {
        for (const job of groupJobs) {
            await dismiss(job.pk);
        }
    };

    const getGroupOverallStatus = (groupJobs) => {
        const statuses = groupJobs.map((job) => job.job_status);
        if (statuses.includes(EXPORT_JOB_STATUS.ERROR)) return EXPORT_JOB_STATUS.ERROR;
        if (statuses.includes(EXPORT_JOB_STATUS.PROCESSING)) return EXPORT_JOB_STATUS.PROCESSING;
        if (statuses.includes(EXPORT_JOB_STATUS.PENDING)) return EXPORT_JOB_STATUS.PENDING;
        if (statuses.every((s) => s === EXPORT_JOB_STATUS.EXPIRED)) return EXPORT_JOB_STATUS.EXPIRED;
        const totalParts = groupJobs[0]?.total_parts || 1;
        if (groupJobs.length < totalParts) return EXPORT_JOB_STATUS.PROCESSING;
        if (statuses.every((s) => s === EXPORT_JOB_STATUS.READY)) return EXPORT_JOB_STATUS.READY;
        return EXPORT_JOB_STATUS.PENDING;
    };

    const groupedExports = useMemo(() => {
        const groupMap = {};

        jobs.forEach((job) => {
            const groupKey = job.group_key || String(job.pk);
            if (!groupMap[groupKey]) {
                groupMap[groupKey] = { groupKey, jobs: [] };
            }
            groupMap[groupKey].jobs.push(job);
        });

        return Object.values(groupMap)
            .map((group) => ({
                ...group,
                jobs: [...group.jobs].sort((jobA, jobB) => (jobA.part_num || 1) - (jobB.part_num || 1)),
            }))
            .sort((groupA, groupB) => {
                const timeA = new Date(groupA.jobs[0]?.created_on || 0);
                const timeB = new Date(groupB.jobs[0]?.created_on || 0);
                return timeB - timeA;
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs]);

    const pageTitle = useMemo(
        () => intl.get("MyDownloads").d("My Downloads"),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [language]
    );

    return (
        <DmsPage name={pageTitle} manual_slug="my-downloads">
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                            {pageTitle}
                        </Typography>
                        <Tooltip title={intl.get("Refresh").d("Refresh")}>
                            <IconButton onClick={refresh} size="small">
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : groupedExports.length === 0 ? (
                        <Box py={4} textAlign="center">
                            <Typography color="text.secondary">
                                {intl.get("MyDownloads.Empty").d("No exports found.")}
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            {groupedExports.map((group) => {
                                const representativeJob = group.jobs[0];
                                const totalParts = representativeJob.total_parts || 1;
                                const isMultiPart = totalParts > 1;
                                const overallStatus = getGroupOverallStatus(group.jobs);

                                return (
                                    <Stack
                                        key={group.groupKey}
                                        sx={{
                                            py: 1.5,
                                            px: 2,
                                            borderBottom: "1px solid",
                                            borderColor: "divider",
                                            "&:last-child": { borderBottom: 0 },
                                        }}
                                        spacing={1}
                                    >
                                        {/* Group header row */}
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                        >
                                            <Stack direction="column" spacing={0.25}>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {representativeJob.job_type_label} — {representativeJob.format.toUpperCase()}
                                                    {isMultiPart && (
                                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                                            ({totalParts} {intl.get("ExportJob.Parts").d("parts")})
                                                        </Typography>
                                                    )}
                                                </Typography>
                                                <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                                                    <Typography variant="caption" color="text.secondary">
                                                        {intl.get("ExportJob.GeneratedOn").d("Generated on")}{": "}
                                                        {representativeJob.created_on
                                                            ? new Date(representativeJob.created_on).toLocaleString()
                                                            : "—"}
                                                    </Typography>
                                                    {representativeJob.expires_on && (() => {
                                                        const daysLeft = Math.ceil(
                                                            (new Date(representativeJob.expires_on) - Date.now()) / 86400000
                                                        );
                                                        return (
                                                            <>
                                                                <Typography variant="caption" color="text.disabled">·</Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color={daysLeft <= 1 ? "error.main" : daysLeft <= 2 ? "warning.main" : "text.secondary"}
                                                                >
                                                                    {intl.get("ExportJob.ExpiresOn").d("Expires on")}{": "}
                                                                    {new Date(representativeJob.expires_on).toLocaleDateString()}
                                                                    {daysLeft > 0 && ` (${daysLeft}d)`}
                                                                </Typography>
                                                            </>
                                                        );
                                                    })()}
                                                </Stack>
                                                {representativeJob.error_message && (
                                                    <Typography variant="caption" color="error">
                                                        {representativeJob.error_message}
                                                    </Typography>
                                                )}
                                            </Stack>

                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Chip
                                                    label={
                                                        overallStatus === EXPORT_JOB_STATUS.PENDING && representativeJob.queue_position != null
                                                            ? `${statusLabels[overallStatus]} #${representativeJob.queue_position}`
                                                            : (statusLabels[overallStatus] ?? representativeJob.job_status_label)
                                                    }
                                                    color={EXPORT_STATUS_COLOR[overallStatus] ?? "default"}
                                                    size="small"
                                                />
                                                {!isMultiPart && overallStatus === EXPORT_JOB_STATUS.READY && (
                                                    <Tooltip title={intl.get("ExportJob.Download").d("Download")}>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleDownload(representativeJob)}
                                                        >
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title={intl.get("ExportJob.Dismiss").d("Dismiss")}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDismissGroup(group.jobs)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>

                                        {/* Part download buttons (multi-part only) */}
                                        {isMultiPart && (
                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {group.jobs.map((partJob) => (
                                                    <Tooltip
                                                        key={partJob.pk}
                                                        title={partJob.file_name || ""}
                                                    >
                                                        <span>
                                                            <DmsButton
                                                                outline
                                                                iconFirst
                                                                icon={<DownloadIcon style={{ fontSize: "0.85rem" }} />}
                                                                disabled={partJob.job_status !== EXPORT_JOB_STATUS.READY}
                                                                onClick={() => handleDownload(partJob)}
                                                                style={{ padding: "2px 8px", fontSize: "0.72rem", minWidth: 0 }}
                                                            >
                                                                {intl.get("ExportJob.Part").d("Part")} {partJob.part_num}
                                                            </DmsButton>
                                                        </span>
                                                    </Tooltip>
                                                ))}
                                            </Stack>
                                        )}
                                    </Stack>
                                );
                            })}
                        </Box>
                    )}
                </DmsAltBox.Body>
            </DmsAltBox.Card>
        </DmsPage>
    );
};

export default MyDownloadsPage;
